import { ActionListControlEnum } from '@inter-ikea-kompis/component-action-list/lib/';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { KompisActionListItem } from '@inter-ikea-kompis/react-components';
import { Product } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { getInvalidFrames, getReplacedFrames } from 'ducks/core';
import { getAllFrameProducts, getMarketProducts, getUnavailableProducts } from 'ducks/products';
import { changeSolutionSize } from 'rangeConfig/pax/utils/changeSize/changeSolutionSize';
import { changeFrameColor } from 'rangeConfig/shared/utils/colorUtils/changeFrameColor';
import { getFramesInScene } from 'rangeConfig/shared/utils/colorUtils/utils';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { getIconForDesign } from 'ui/ChangeColor';
import Body from 'ui/Typography/Body';
import Title from 'ui/Typography/Title';
import media from 'ui/media';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ApprovedIcon, UnapprovedIcon } from './icons';
import { ChangeFrame, OptionType, OptionValue } from './types';
import {
  checkOption,
  getCaption,
  getDescription,
  getLabel,
  getListHeading,
  getOptions,
  getSelectedOptions
} from './utils/getFrameOptions';

const DescriptionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: ${({ theme }) => theme.space.xxl};
  ${media.mobile`
    margin-bottom: 1rem;
  `}
`;

const ListWrapper = styled.div`
  kompis-action-list-item {
    :first-child {
      border-top: none;
    }
    :last-child {
      border-bottom: none;
    }
  }
`;

type Props = {
  typeOfOption: (type: OptionType) => void;
};

const UnavailableFramesOptions = ({ typeOfOption }: Props) => {
  const { formatMessage } = useIntl();
  const coreApi = useCore();
  const [unavailableFramesInScene, setUnavailableFramesInScene] = useState<Product[]>([]);

  const allAvailableFrameProducts = useSelector(getAllFrameProducts);
  const products = useSelector(getMarketProducts);
  const unavailableProducts = useSelector(getUnavailableProducts);
  const invalidFrames = useSelector(getInvalidFrames);
  const replacedFrames = useSelector(getReplacedFrames);

  const changeFrameOptions: ChangeFrame[] = [
    {
      type: OptionType.changeColour,
      possibleToChange: checkOption(
        OptionType.changeColour,
        unavailableFramesInScene,
        allAvailableFrameProducts
      )
    },
    {
      type: OptionType.changeDepth,
      possibleToChange: checkOption(
        OptionType.changeDepth,
        unavailableFramesInScene,
        allAvailableFrameProducts
      )
    },
    {
      type: OptionType.changeDepthAndColour,
      possibleToChange: checkOption(
        OptionType.changeDepthAndColour,
        unavailableFramesInScene,
        allAvailableFrameProducts
      )
    }
  ];

  const changeSolutionInScene = (type: OptionType, option: OptionValue) => {
    const frameEntities = getFramesInScene(coreApi);
    const allProducts = [...products, ...unavailableProducts];
    const colourValue = option.colour?.value;
    const depthValue = option.depth?.value;

    switch (type) {
      case OptionType.changeColour:
        if (colourValue) {
          coreApi.history.historyAction(() => {
            changeFrameColor(coreApi, frameEntities, colourValue, allProducts);
          });
        }
        break;
      case OptionType.changeDepth:
        if (depthValue) {
          coreApi.history.historyAction(() => {
            changeSolutionSize(coreApi, 'depth', depthValue, allProducts);
          });
        }
        break;
      case OptionType.changeDepthAndColour:
        if (colourValue && depthValue) {
          coreApi.history.historyAction(() => {
            changeFrameColor(coreApi, frameEntities, colourValue, allProducts);
            changeSolutionSize(coreApi, 'depth', depthValue, allProducts);
          });
        }
        break;
    }
  };

  const onSelect = (type: OptionType, option: OptionValue) => {
    coreApi.performLongCoreOperation(() => {
      coreApi.validations['WallVisibilityValidation']?.disable();
      coreApi.validations['SeeThroughFramesValidation']?.disable();
      changeSolutionInScene(type, option);
      coreApi.validations['WallVisibilityValidation']?.enable();
      coreApi.validations['SeeThroughFramesValidation']?.enable();
    });
  };

  useEffect(() => {
    // Update options if we load another vpc code with menu open
    const resetUnavailableFramesInScene = () => {
      setUnavailableFramesInScene([]);
    };
    return coreApi.subscribe('loadVpc', resetUnavailableFramesInScene);
  }, [coreApi]);

  if (invalidFrames.length === 0 && unavailableFramesInScene.length === 0) {
    return null;
  }

  if (invalidFrames.length > 0 && unavailableFramesInScene.length === 0) {
    setUnavailableFramesInScene(
      unavailableProducts.filter(p => invalidFrames.includes(p.coreProductId))
    );
  }

  // Find first available option to change frames in scene from list
  const changeFrameOption = changeFrameOptions.find(option => option.possibleToChange);
  if (changeFrameOption) {
    const type = changeFrameOption.type;
    typeOfOption(type);
    const options = getOptions(type, unavailableFramesInScene, allAvailableFrameProducts);
    const replacedFrameProducts = allAvailableFrameProducts.filter(p =>
      replacedFrames.includes(p.coreProductId)
    );
    const selectedOptions = getSelectedOptions(
      invalidFrames.length > 0,
      options,
      replacedFrameProducts
    );

    return (
      <>
        <DescriptionWrapper>
          {invalidFrames.length > 0 ? <UnapprovedIcon /> : <ApprovedIcon />}
          <Body type={ThemeFontStyleTypeEnum.bodyMedium} ml="xs">
            {getDescription(type, invalidFrames.length === 0, formatMessage)}
          </Body>
        </DescriptionWrapper>
        <Title type={ThemeFontStyleTypeEnum.headingSmall}>
          {getListHeading(type, formatMessage)}
        </Title>
        <ListWrapper>
          {options.map((option, index) => (
            <KompisActionListItem
              key={index}
              theme={SkapaTheme}
              control={ActionListControlEnum.radioButton}
              checked={selectedOptions.includes(index)}
              onSelect={() => onSelect(type, option)}
              imageSrc={option.colour && getIconForDesign(option.colour.value)}
              label={getLabel(type, option, formatMessage)}
              caption={getCaption(type, option, formatMessage)}
              disabled={!option.available}
            />
          ))}
        </ListWrapper>
      </>
    );
  } else if (unavailableFramesInScene.length > 0) {
    // No options available
    coreApi.emit.onLoadVpcFailure(undefined);
  }
  return null;
};

export default UnavailableFramesOptions;
